import React, { useState } from 'react';
import { CheckCircle2Icon, MailIcon, MessageCircleIcon } from 'lucide-react';
import { Button } from '../ui/Button';
const states = ['Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'];
type Channel = 'whatsapp' | 'email';
type Errors = {
  name?: string;
  contact?: string;
  state?: string;
  consent?: string;
};
export function StayConnected() {
  const [channel, setChannel] = useState<Channel>('whatsapp');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [stateValue, setStateValue] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const validate = (): Errors => {
    const next: Errors = {};
    if (!name.trim()) next.name = 'Enter your first name.';
    if (!contact.trim()) {
      next.contact = channel === 'whatsapp' ? 'Enter your phone number.' : 'Enter your email address.';
    } else if (channel === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact)) {
      next.contact = 'Enter a valid email address.';
    } else if (channel === 'whatsapp' && contact.replace(/\D/g, '').length < 10) {
      next.contact = 'Enter a valid Nigerian phone number.';
    }
    if (!stateValue) next.state = 'Select your state.';
    if (!consent) next.consent = 'Please confirm you agree to receive updates.';
    return next;
  };
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('loading');
    window.setTimeout(() => setStatus('success'), 900);
  };
  const fieldClass = (hasError: boolean) => `w-full rounded-lg border bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-500/70 transition-colors duration-150 ease-emphasis focus:outline-none ${hasError ? 'border-red-600' : 'border-sand-300 focus:border-green-600'}`;
  return <section aria-labelledby="connect-heading" className="bg-sand-100 py-16 sm:py-20 lg:py-24" data-id="element-305" data-mp="2elajbq89jmbo">
      <div className="mx-auto grid max-w-content gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-306" data-mp="29m7di3kglc2n">
        <div className="lg:col-span-5" data-id="element-307" data-mp="24mbiuuxniwzr">
          <p className="text-[13px] font-semibold uppercase tracking-[0.16em] text-green-700" data-id="element-308" data-mp="24l9wvvf3kq39">
            Stay connected
          </p>
          <h2 id="connect-heading" className="mt-3 max-w-[16ch] font-display text-[30px] leading-[1.1] tracking-[-0.02em] text-ink sm:text-[38px]" data-id="element-309" data-mp="1vm6b2jojpwl2">
            Campaign updates, straight to you.
          </h2>
          <p className="mt-4 max-w-md text-[16px] leading-relaxed text-slate-600" data-id="element-310" data-mp="3q18wa56kasi6">
            Policy releases, town halls near you and the campaign’s response to the day’s news. No
            more than three messages a week, and you can leave at any time.
          </p>
        </div>

        <div className="lg:col-span-7" data-id="element-311" data-mp="1lj11pxvwc1c2">
          {status === 'success' ? <div role="status" className="flex items-start gap-4 rounded-media border border-green-200 bg-white p-8" data-id="element-312" data-mp="1pk306chzo9cz">
              <CheckCircle2Icon aria-hidden className="mt-0.5 h-6 w-6 shrink-0 text-green-600" data-id="element-313" data-mp="svdlsw5klyvr" />
              <div data-id="element-314" data-mp="xuhs9f141iie">
                <h3 className="font-display text-[22px] text-ink" data-id="element-315" data-mp="11tz0gg7x8tr9">You are on the list.</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-316" data-mp="11wbp1c86q2da">
                  Thank you, {name.trim()}. We will send your first update from {stateValue} within
                  the week. Look out for a confirmation message.
                </p>
              </div>
            </div> : <form onSubmit={onSubmit} noValidate className="rounded-media border border-sand-200 bg-white p-6 sm:p-8" data-id="element-317" data-mp="2gqth1zzzc0q1">
              <fieldset data-id="element-318" data-mp="2bubglcbomoji">
                <legend className="text-[14px] font-semibold text-ink" data-id="element-319" data-mp="2prhtwrdmzv1c">
                  How should we reach you?
                </legend>
                <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-320" data-mp="2lpd27y6r18hc">
                  {([{
                id: 'whatsapp',
                label: 'WhatsApp updates',
                Icon: MessageCircleIcon
              }, {
                id: 'email',
                label: 'Email updates',
                Icon: MailIcon
              }] as const).map(({
                id,
                label,
                Icon
              }) => <label key={id} className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-colors duration-150 ease-emphasis ${channel === id ? 'border-green-600 bg-green-50' : 'border-sand-300 hover:border-slate-500/50'}`} data-id="element-321" data-mp="34g4d546dx6xu">
                      <input type="radio" name="channel" value={id} checked={channel === id} onChange={() => {
                  setChannel(id);
                  setContact('');
                }} className="h-4 w-4 accent-[#056839]" data-id="element-322" data-mp="5vi3qriclgdt" />
                      <Icon aria-hidden className="h-[18px] w-[18px] text-green-700" data-id="element-323" data-mp="3j4nycmjqfg9d" />
                      <span className="text-[15px] font-medium text-ink" data-id="element-324" data-mp="34g1i8ima6kh3">{label}</span>
                    </label>)}
                </div>
              </fieldset>

              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-325" data-mp="2vetma778lvq6">
                <div data-id="element-326" data-mp="30clnjrtruu71">
                  <label htmlFor="first-name" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-327" data-mp="30d4u6cdz1w5r">
                    First name <span className="text-red-600" data-id="element-328" data-mp="15xetxnyti7y5">*</span>
                  </label>
                  <input id="first-name" name="firstName" autoComplete="given-name" value={name} onChange={event => setName(event.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'first-name-error' : undefined} className={fieldClass(Boolean(errors.name))} data-id="element-329" data-mp="115ogva8hx631" />
                  {errors.name && <p id="first-name-error" className="mt-1.5 text-[13px] text-red-600" data-id="element-330" data-mp="1ftrhe43rrpkg">
                      {errors.name}
                    </p>}
                </div>

                <div data-id="element-331" data-mp="4gp3xhwyvg4i">
                  <label htmlFor="contact" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-332" data-mp="4h8lgxipg2i0">
                    {channel === 'whatsapp' ? 'Phone number' : 'Email address'}{' '}
                    <span className="text-red-600" data-id="element-333" data-mp="2b57kspuj3j5r">*</span>
                  </label>
                  <input id="contact" name={channel === 'whatsapp' ? 'tel' : 'email'} type={channel === 'whatsapp' ? 'tel' : 'email'} inputMode={channel === 'whatsapp' ? 'tel' : 'email'} autoComplete={channel === 'whatsapp' ? 'tel' : 'email'} placeholder={channel === 'whatsapp' ? '+234 800 000 0000' : 'you@example.com'} value={contact} onChange={event => setContact(event.target.value)} aria-invalid={Boolean(errors.contact)} aria-describedby={errors.contact ? 'contact-error' : undefined} className={fieldClass(Boolean(errors.contact))} data-id="element-334" data-mp="2g27urbui1625" />
                  {errors.contact && <p id="contact-error" className="mt-1.5 text-[13px] text-red-600" data-id="element-335" data-mp="1dgo25dvl80lm">
                      {errors.contact}
                    </p>}
                </div>

                <div className="sm:col-span-2" data-id="element-336" data-mp="pk16izivaeiw">
                  <label htmlFor="state" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-337" data-mp="13c7molm0slic">
                    State <span className="text-red-600" data-id="element-338" data-mp="zam6ore6vr3x">*</span>
                  </label>
                  <select id="state" name="state" value={stateValue} onChange={event => setStateValue(event.target.value)} aria-invalid={Boolean(errors.state)} aria-describedby={errors.state ? 'state-error' : undefined} className={fieldClass(Boolean(errors.state))} data-id="element-339" data-mp="zczxup5r0j67">
                    <option value="" data-id="element-340" data-mp="1w0d7t9gzkcip">Select your state</option>
                    {states.map(item => <option key={item} value={item} data-id="element-341" data-mp="1r2l4ljaw53z7">
                        {item}
                      </option>)}
                  </select>
                  {errors.state && <p id="state-error" className="mt-1.5 text-[13px] text-red-600" data-id="element-342" data-mp="23kns976icxph">
                      {errors.state}
                    </p>}
                </div>
              </div>

              <div className="mt-6" data-id="element-343" data-mp="1uksdksdqukkp">
                <label className="flex items-start gap-3" data-id="element-344" data-mp="1ultrd4m59l64">
                  <input type="checkbox" checked={consent} onChange={event => setConsent(event.target.checked)} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'consent-error' : undefined} className="mt-0.5 h-4 w-4 accent-[#056839]" data-id="element-345" data-mp="2iacz9j1a7vxo" />
                  <span className="text-[14px] leading-relaxed text-slate-600" data-id="element-346" data-mp="3174n85rrqz82">
                    I agree to receive campaign updates and I have read the{' '}
                    <a href="#" className="font-medium text-green-700 underline underline-offset-2 hover:text-green-800" data-id="element-347" data-mp="wzv6yoh10z28">
                      privacy notice
                    </a>
                    .
                  </span>
                </label>
                {errors.consent && <p id="consent-error" className="mt-1.5 text-[13px] text-red-600" data-id="element-348" data-mp="swgto5kh2wsh">
                    {errors.consent}
                  </p>}
              </div>

              <Button type="submit" size="lg" loading={status === 'loading'} className="mt-6 w-full sm:w-auto" data-id="element-349" data-mp="j0m3mpdx5zfo">
                {status === 'loading' ? 'Joining…' : 'Join campaign updates'}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}